import { StyleSheet } from 'aphrodite';
import { THEME } from '../../../../web/components-v2/Theme/theme';

/**
 * Shared visual primitives for the Support settings sections, mapped onto the Claude Design
 * "Support Inbox → Settings" prototype. Every section reuses the same white card, Sora section
 * heading, field/label rhythm, 40px inputs and pill toggles so the surfaces read as one system
 * rather than five hand-styled tabs. Tokens come from `THEME`; only the prototype's
 * `--lv-shadow-sm` is a literal (it is not a palette token).
 */

/** The prototype's `--lv-shadow-sm`. Not a palette token, so it lives here as a literal. */
export const SETTINGS_SHADOW_SM = '0 1px 2px 0 rgb(0 0 0 / 0.05)';

/** Card corner radius from the prototype (softer than the global 8px to read as a panel). */
const CARD_RADIUS = 14;

export const settingsStyles = StyleSheet.create({
	// ---- Section heading block (h2 + lead paragraph) ----
	sectionTitle: {
		fontFamily: THEME.typography.fontFamilies.sora,
		fontSize: 20,
		fontWeight: 600,
		margin: '0 0 4px',
		color: THEME.colors.text.main,
	},
	sectionDescription: {
		fontSize: THEME.typography.fontSizes.sm,
		color: THEME.colors.text.light,
		margin: '0 0 22px',
		lineHeight: 1.5,
	},

	// ---- Card surface ----
	card: {
		backgroundColor: THEME.colors.background.light,
		border: `1px solid ${THEME.colors.border.light}`,
		borderRadius: CARD_RADIUS,
		boxShadow: SETTINGS_SHADOW_SM,
		padding: 24,
	},

	// ---- Inline create/edit form card (heading + fields, above a list) ----
	formCard: {
		border: `1px solid ${THEME.colors.border.light}`,
		borderRadius: CARD_RADIUS,
		boxShadow: SETTINGS_SHADOW_SM,
		padding: 24,
		marginBottom: 16,
		backgroundColor: THEME.colors.background.light,
	},
	formTitle: {
		fontFamily: THEME.typography.fontFamilies.sora,
		fontSize: 20,
		fontWeight: 600,
		margin: '0 0 16px',
		color: THEME.colors.text.main,
	},

	// ---- Field rhythm ----
	field: {
		marginBottom: 18,
	},
	fieldLabel: {
		display: 'block',
		fontSize: 13,
		color: THEME.colors.text.light,
		marginBottom: 6,
	},
	hint: {
		fontSize: THEME.typography.fontSizes.xs,
		color: THEME.colors.text.light,
		marginTop: 6,
		lineHeight: 1.5,
	},

	// ---- Inputs (passed to components-v2 Input/TextArea via `styles`) ----
	input: {
		width: '100%',
		height: 40,
		padding: '0 13px',
		fontSize: THEME.typography.fontSizes.sm,
		color: THEME.colors.text.main,
		border: `1px solid ${THEME.colors.border.main}`,
		borderRadius: THEME.common.borderRadius,
		boxShadow: SETTINGS_SHADOW_SM,
		outline: 'none',
		boxSizing: 'border-box' as const,
		backgroundColor: THEME.colors.background.light,
		':focus': {
			borderColor: THEME.colors.primary.main,
		},
	},

	// ---- Rich-text editor shell + its "Insert variable" pill select ----
	editorContainer: {
		border: `1px solid ${THEME.colors.border.main}`,
		borderRadius: THEME.common.borderRadius,
		boxShadow: SETTINGS_SHADOW_SM,
		backgroundColor: THEME.colors.background.light,
		overflow: 'hidden' as const,
	},
	selectPill: {
		padding: '4px 8px',
		fontSize: 12,
		border: `1px solid ${THEME.colors.border.main}`,
		borderRadius: THEME.common.borderRadius,
		backgroundColor: THEME.colors.background.light,
		cursor: 'pointer',
	},

	// ---- Pill toggle (38×22 with an 18px knob) ----
	toggleRow: {
		display: 'flex',
		alignItems: 'center',
		gap: 11,
	},
	toggle: {
		position: 'relative' as const,
		width: 38,
		height: 22,
		borderRadius: 11,
		border: 'none',
		cursor: 'pointer',
		padding: 0,
		flex: 'none',
		backgroundColor: THEME.colors.border.main,
		transition: 'background-color 0.15s',
	},
	toggleActive: {
		backgroundColor: THEME.colors.primary.main,
	},
	toggleKnob: {
		position: 'absolute' as const,
		top: 2,
		left: 2,
		width: 18,
		height: 18,
		borderRadius: '50%',
		backgroundColor: THEME.colors.background.light,
		boxShadow: SETTINGS_SHADOW_SM,
		transition: 'left 0.15s',
	},
	toggleKnobActive: {
		left: 18,
	},
	toggleText: {
		display: 'flex',
		flexDirection: 'column' as const,
	},
	toggleTitle: {
		fontSize: THEME.typography.fontSizes.sm,
		fontWeight: 500,
		color: THEME.colors.text.main,
	},
	toggleDescription: {
		fontSize: THEME.typography.fontSizes.xs,
		color: THEME.colors.text.light,
	},

	// ---- "New …" / "Add …" toolbar button ----
	newButton: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: 7,
		flexShrink: 0,
	},

	// ---- Save row + status messages ----
	formActions: {
		display: 'flex',
		alignItems: 'center',
		gap: 12,
		marginTop: 4,
	},
	// Tight action row used under inline create/edit forms (distinct from `formActions`).
	formActionsTight: {
		display: 'flex',
		gap: 8,
		marginTop: 16,
	},
	successMsg: {
		fontSize: 13,
		color: THEME.colors.success.dark,
	},
	errorMsg: {
		fontSize: 13,
		color: THEME.colors.danger.main,
	},

	// ---- List surface + expandable edit row ----
	list: {
		display: 'flex',
		flexDirection: 'column' as const,
		backgroundColor: THEME.colors.background.light,
		border: `1px solid ${THEME.colors.border.light}`,
		borderRadius: CARD_RADIUS,
		boxShadow: SETTINGS_SHADOW_SM,
		overflow: 'hidden',
	},
	editForm: {
		padding: 16,
		borderTop: `1px solid ${THEME.colors.border.light}`,
		backgroundColor: THEME.colors.background.main,
	},

	// ---- States ----
	loading: {
		padding: 24,
		textAlign: 'center' as const,
		color: THEME.colors.text.light,
	},
	error: {
		padding: 24,
		color: THEME.colors.danger.main,
	},
	emptyState: {
		padding: 32,
		textAlign: 'center' as const,
		color: THEME.colors.text.light,
		fontSize: 14,
	},
});
